import { Timestamp } from '@angular/fire/firestore';
import { CHURCH_ADMIN_ROLE_ID } from './church-affiliation-invite.model';

export interface HierarchyNode {
  id: string; // e.g. "node_district_3"
  organizationId: string;
  name: string; // e.g. "District 3"
  levelId: string; // Dynamic level ID referencing Organization.levelDefinitions
  levelName: string; // Display label (e.g. "District", "Diocese")
  depth: number; // 0 = root, 1 = region, 2 = district...
  parentId: string | null;

  // Materialized Path & Ancestry for O(1) subtree matching
  path: string; // "org_1/node_board/node_region_1/node_district_3"
  ancestors: string[]; // ["org_1", "node_board", "node_region_1"]

  directChurchCount: number;
  totalSubtreeChurchCount: number;

  status: 'active' | 'archived';
  // Set on church-level nodes created when an existing church group joins (id === church group id)
  isChurch?: boolean;
  churchId?: string;
  roles?: NodeRole[]; // Node-specific roles and assigned members
  // Church nodes only: the church's active member count, published daily by the server's org
  // analytics job. Unit totals live in hierarchy_node_stats (org members only), see getUnitStats.
  stats?: HierarchyNodeStats;
  createdBy?: string;
  created?: Timestamp;
  modified?: Timestamp;
}

export interface HierarchyNodeStats {
  activeMembers: number;
  // Units only (from hierarchy_node_stats); a church's own stats are just its member count
  churchCount?: number;
  asOf: number | null; // when the job last ran, in epoch millis
}

export interface NodeRole {
  id: string;
  name: string; // e.g. "President", "Regional Superintendent", "Secretary"
  description?: string;
  permissions: NodePermission[];
  members: NodeRoleMember[];
  // 'church_affiliation' roles are synced from the church's admins and read-only in the org chart
  managedBy?: 'church_affiliation';
  created?: any;
  modified?: any;
}

// Id of the admin role created on an organization's root node (see the server's ROOT_ADMIN_ROLE_ID)
export const ROOT_ADMIN_ROLE_ID = 'org_admin';
// Id of the admin role every other unit is created with (see the server's UNIT_ADMIN_ROLE_ID)
export const UNIT_ADMIN_ROLE_ID = 'unit_admin';

/**
 * Title shown for a role. Admin roles get a name that says whose admins they are, since
 * every church and the root carry one with the same stored name ("Church Admin", "Administrator"):
 * - church admin role:  "Bethel Assembly Admins"
 * - other admin roles:  "<unit name> <level> Admins", e.g. "Kerala State Admins" (the level is
 *   left out when the unit name already ends with it, e.g. "AtChurch Board Admins")
 * Roles people create keep the name they were given. Display only; stored names are unchanged.
 */
export function getRoleDisplayName(
  node: Pick<HierarchyNode, 'name' | 'levelName' | 'isChurch'>,
  role: Pick<NodeRole, 'id' | 'name'>
): string {
  const unitName = (node.name || '').trim();
  if (role.id === CHURCH_ADMIN_ROLE_ID && unitName) {
    return `${unitName} Admins`;
  }
  if ((role.id === ROOT_ADMIN_ROLE_ID || role.id === UNIT_ADMIN_ROLE_ID) && unitName) {
    const level = (node.levelName || '').trim();
    const endsWithLevel = !!level && unitName.toLowerCase().endsWith(level.toLowerCase());
    return level && !endsWithLevel ? `${unitName} ${level} Admins` : `${unitName} Admins`;
  }
  return role.name;
}

export interface NodeRoleMember {
  userId: string;
  userName: string;
  userEmail: string;
  userPhotoUrl?: string;
  assignedAt?: any;
}

export type NodePermission =
  | 'manage_roles'
  | 'manage_nodes'
  | 'manage_churches'
  | 'view_analytics';
// Church finances are never visible to a management organization, so there is no finance permission
